<template>
  <div class="vehicles-page">
    <v-card class="rounded-lg elevation-1 bg-white">
      <v-card-title class="d-flex justify-space-between align-center py-4 px-5 border-bottom">
        <div class="d-flex align-center">
          <v-icon color="primary" size="28" class="me-2">mdi-car-info</v-icon>
          <span class="text-h5 font-weight-black text-dark-primary">السيارات المركبات</span>
        </div>
        <create-button @create="showCreateDialog"></create-button>
      </v-card-title>

      <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :headers="translatedHeaders"
        :items="cars"
        :footer-props="footerProps"
        class="border-grey"
      >
        <!-- UNIFIED SERVER-SIDE SEARCH BAR -->
        <template v-slot:top>
          <div class="pa-4 bg-light border-bottom">
            <v-row dense align="center">
              <v-col cols="12" md="6" lg="5">
                <v-text-field
                  v-model="searchQuery"
                  placeholder="ابحث بنوع السيارة أو الاسم أو الموديل أو السنة..."
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  outlined
                  dense
                  hide-details
                  class="bg-white text-dark-primary font-weight-bold"
                  @input="onSearchInput"
                  @click:clear="clearSearch"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>
        </template>

        <template v-slot:item.id="{ item }">
          <span class="font-weight-bold primary--text">#{{ item.id }}</span>
        </template>

        <template v-slot:item.image="{ item }">
          <v-img
            v-if="item.image_url"
            :src="item.image_url"
            max-width="80px"
            max-height="50px"
            contain
            class="my-1 rounded border"
          ></v-img>
          <span v-else class="text-dark-secondary font-weight-medium caption">بدون صورة</span>
        </template>

        <template v-slot:item.car_type="{ item }">
          <span class="font-weight-bold text-dark-primary">
            {{ (item.car_type ? (item.car_type.name_ar || item.car_type.name_en) : '—') }}
          </span>
        </template>

        <template v-slot:item.name_en="{ item }">
          <span class="font-weight-bold text-dark-primary">
            {{ item.name_ar || item.name_en || '—' }}
          </span>
        </template>

        <template v-slot:item.model="{ item }">
          <span class="font-weight-bold text-dark-primary">{{ item.model || '—' }}</span>
        </template>

        <template v-slot:item.year="{ item }">
          <span class="font-weight-bold text-dark-primary">{{ item.year || '—' }}</span>
        </template>

        <template v-slot:item.is_active="{ item }">
          <v-chip :color="item.is_active ? 'success' : 'error'" dark small class="font-weight-bold">
            {{ item.is_active ? $t('Active') : $t('Inactive') }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon small color="primary" class="mx-2" @click="editCar(item)">mdi-pencil</v-icon>
          <v-icon small color="error" class="mx-2" @click="deleteCar(item, cars.indexOf(item))">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <!-- CREATE / EDIT DIALOG -->
    <v-dialog v-model="dialog" persistent max-width="600px">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-card class="rounded-lg bg-white text-dark-primary">
          <v-card-title class="pa-4 primary lighten-5 border-bottom">
            <span class="text-h6 font-weight-black text-dark-primary">{{ isEdit ? $t('edit_car') : $t('add_new_car') }}</span>
          </v-card-title>
          <v-card-text class="pa-4">
            <v-container>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="editedItem.private_car_type_id"
                    :items="carTypes"
                    item-text="name_ar"
                    item-value="id"
                    :rules="[v => !!v || 'نوع السيارة مطلوب']"
                    :label="$t('car_type') + '*'"
                    outlined
                    dense
                    required
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.name_ar"
                    :rules="[v => !!v || 'الاسم بالعربية مطلوب']"
                    label="الاسم (عربي)*"
                    outlined
                    dense
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.name_en"
                    :rules="[v => !!v || 'الاسم بالإنجليزية مطلوب']"
                    label="الاسم (English)*"
                    outlined
                    dense
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.model"
                    :rules="[v => !!v || 'الموديل مطلوب']"
                    label="الموديل*"
                    outlined
                    dense
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.year"
                    :rules="[v => !!v || 'السنة مطلوبة']"
                    label="السنة*"
                    outlined
                    dense
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.sort_order"
                    type="number"
                    label="ترتيب العرض"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-switch
                    v-model="editedItem.is_active"
                    label="مفعل"
                    color="primary"
                  ></v-switch>
                </v-col>
                <v-col cols="12">
                  <v-file-input
                    v-model="imageFile"
                    accept="image/*"
                    label="صورة السيارة"
                    show-size
                    prepend-icon="mdi-camera"
                    outlined
                    dense
                  ></v-file-input>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions class="pa-3 grey lighten-4 border-top">
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" outlined small class="font-weight-bold" @click="dialog = false">{{ $t('cancel') }}</v-btn>
            <v-btn color="primary" small class="font-weight-bold white--text" @click="save">{{ $t('Save Changes') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
import CreateButton from "@/components/CreateButton";

export default {
  components: { CreateButton },
  data() {
    return {
      cars: [],
      carTypes: [],
      isLoading: false,
      searchQuery: "",
      searchTimer: null,
      dialog: false,
      valid: true,
      isEdit: false,
      imageFile: null,
      editedItem: {
        id: null,
        private_car_type_id: null,
        name_en: "",
        name_ar: "",
        model: "",
        year: "",
        sort_order: 0,
        is_active: true
      },
      headers: [
        { text: "id", value: "id", align: "start" },
        { text: "image", value: "image", sortable: false },
        { text: "car_type", value: "car_type" },
        { text: "Name", value: "name_en" },
        { text: "model", value: "model" },
        { text: "year", value: "year" },
        { text: "is_active", value: "is_active" },
        { text: "Actions", value: "actions", sortable: false }
      ]
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    footerProps() {
      return {
        itemsPerPageText: this.$t("rows_per_page")
      };
    }
  },
  mounted() {
    this.loadCars();
    this.loadCarTypes();
  },
  methods: {
    loadCars() {
      this.isLoading = true;
      const params = {};
      if (this.searchQuery && this.searchQuery.trim()) {
        params.search = this.searchQuery.trim();
      }

      axios
        .get("/private-car-bookings/cars", { params })
        .then(response => {
          this.cars = response.data || [];
        })
        .catch(error => {
          this.$notify({
            title: this.$t("Error"),
            text: "Failed to load cars",
            type: "error"
          });
          console.error(error);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.loadCars();
      }, 400);
    },
    clearSearch() {
      this.searchQuery = "";
      this.loadCars();
    },
    loadCarTypes() {
      axios
        .get("/private-car-bookings/types")
        .then(response => {
          this.carTypes = (response.data || []).filter(t => t.is_active);
        })
        .catch(error => {
          console.error(error);
        });
    },
    showCreateDialog() {
      this.isEdit = false;
      this.imageFile = null;
      this.editedItem = {
        id: null,
        private_car_type_id: null,
        name_en: "",
        name_ar: "",
        model: "",
        year: "",
        sort_order: 0,
        is_active: true
      };
      this.dialog = true;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    editCar(item) {
      this.isEdit = true;
      this.imageFile = null;
      this.editedItem = Object.assign({}, item);
      this.editedItem.is_active = !!item.is_active;
      this.dialog = true;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    deleteCar(item, index) {
      this.$swal
        .fire({
          title: this.$t("yes_delete"),
          text: "Are you sure you want to delete this car?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$t("yes"),
          cancelButtonText: this.$t("cancel")
        })
        .then(result => {
          if (result.isConfirmed) {
            axios
              .delete(`/private-car-bookings/cars/${item.id}`)
              .then(() => {
                this.cars.splice(index, 1);
                this.$notify({
                  title: this.$t("Success"),
                  text: "Car deleted successfully",
                  type: "success"
                });
              })
              .catch(error => {
                this.$swal(this.$t("Error"), "Failed to delete car", "error");
              });
          }
        });
    },
    save() {
      if (this.$refs.form.validate()) {
        this.isLoading = true;
        this.dialog = false;

        const formData = new FormData();
        formData.append("private_car_type_id", this.editedItem.private_car_type_id);
        formData.append("name_en", this.editedItem.name_en);
        formData.append("name_ar", this.editedItem.name_ar);
        formData.append("model", this.editedItem.model);
        formData.append("year", this.editedItem.year);
        formData.append("sort_order", this.editedItem.sort_order);
        formData.append("is_active", this.editedItem.is_active ? 1 : 0);

        if (this.imageFile) {
          formData.append("image", this.imageFile);
        }

        const url = this.isEdit
          ? `/private-car-bookings/cars/${this.editedItem.id}`
          : "/private-car-bookings/cars";

        axios
          .post(url, formData, {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          })
          .then(() => {
            this.loadCars();
            this.$notify({
              title: this.$t("Success"),
              text: this.isEdit ? "Car updated successfully" : "Car created successfully",
              type: "success"
            });
          })
          .catch(error => {
            let msg = error.response?.data?.message || "Save failed";
            this.$swal(this.$t("Error"), msg, "error");
          })
          .then(() => {
            this.isLoading = false;
          });
      }
    }
  }
};
</script>

<style scoped>
.vehicles-page { color: #111827 !important; }
.text-dark-primary { color: #111827 !important; }
.text-dark-secondary { color: #374151 !important; }
.border-bottom { border-bottom: 1px solid #e5e7eb !important; }
.border-top { border-top: 1px solid #e5e7eb !important; }
.border-grey { border: 1px solid #e5e7eb !important; }
.bg-light { background-color: #f8fafc !important; }

::v-deep .v-data-table {
  color: #111827 !important;
}
::v-deep .v-data-table thead th {
  color: #111827 !important;
  font-weight: 800 !important;
  font-size: 13px !important;
  background-color: #f8fafc !important;
}
::v-deep .v-data-table tbody td {
  color: #111827 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  opacity: 1 !important;
}
::v-deep .v-data-footer,
::v-deep .v-data-footer *,
::v-deep .v-pagination,
::v-deep .v-pagination * {
  color: #111827 !important;
  opacity: 1 !important;
}
</style>
